import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode, useMemo } from 'react';
import { 
  collection, 
  doc, 
  onSnapshot, 
  Unsubscribe, 
  query, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { UnitStatus } from '../types/property.types';
import { FinanceDocument, FinanceStatusHistory, ClientResponseHistory } from '../types/deal.types';
import type { DepositAdjustmentFields } from '../types/depositAdjustments.types';

// Payment interfaces
interface Payment {
  id: string;
  amount: number;
  date: string;
  proofOfPayment?: string;
  proofOfPaymentName?: string;
}

interface CashPayment extends Payment {
  payee: string;
}

// Deal Capture interface
export interface DealCapture extends DepositAdjustmentFields {
  id?: string;
  unitId: string;
  unitNumber: string;
  developmentId: string;
  developmentName?: string;
  
  // Status tracking
  status?: string;
  currentStatus?: UnitStatus;
  statusHistory?: Array<{
    status: UnitStatus;
    timestamp: Timestamp;
    changedBy: string;
    notes?: string;
  }>;
  
  // Consultant/Agent info
  initialConsultantId?: string;
  initialConsultantRole?: string; // Role at time of initial capture (immutable, prevents retroactive commission changes)
  consultantId?: string;
  consultantName?: string;
  consultantRole?: string; // Role at time of deal capture (for commission locking)
  consultantCommissionPercentage?: number; // Commission % at capture time (30, 50, or 70)
  
  // Contact/Buyer info
  contactId?: string;
  buyerName?: string;
  buyerEmail?: string;
  buyerPhone?: string;
  buyerType?: 'personal' | 'pty' | 'trust';
  entityName?: string;

  // Viewing relationship (if deal originated from a viewing)
  viewingBookingId?: string; // Links to viewingBookings collection if deal came from viewing
  viewingManagerId?: string; // Cache for quick viewing manager lookup

  // Financial info
  purchasePrice?: number;
  dealType?: 'cash' | 'finance';
  
  // Reservation/Deposit tracking
  reservationAmount?: number;
  reservationAmountPayments?: Payment[];
  reservationAmountOutstanding?: number;
  reservationAmountReceived?: boolean;
  reservationAmountAmount?: number; // Total reserved
  
  // Cash deal specific
  outstandingOnCash?: number;
  cashPayments?: CashPayment[];
  cashOutstanding?: number;
  
  // Bond/Finance deal specific
  outstandingOnBond?: number;
  bondPayments?: Payment[];
  bondOutstanding?: number;
  financeAmount?: number;

  // Finance documents and status tracking
  financeDocuments?: FinanceDocument[]; // All uploaded finance documents
  financeStatusHistory?: FinanceStatusHistory[]; // Finance status change history
  currentFinanceStatus?: 'declined' | 'aip' | 'final_grant' | null; // Current finance status
  currentFinalGrantAmount?: number; // Final grant amount from most recent final grant document

  // Client response to finance documents
  clientResponses?: ClientResponseHistory[]; // All client responses to finance documents

  // Documents
  reservationDocument?: string;
  reservationDocumentName?: string;
  otpDocument?: string;
  otpDocumentName?: string;

  // Reservation document verification (HQ review)
  reservationDocumentStatus?: 'pending' | 'approved' | 'rejected'; // Review status
  reservationReviewedBy?: string; // HQ user who reviewed
  reservationReviewedByName?: string; // HQ user name
  reservationReviewedAt?: Timestamp; // When reviewed
  reservationRejectionReason?: string; // Reason if rejected
  reservationRejectionHistory?: Array<{
    rejectedAt: Timestamp;
    rejectedBy: string;
    rejectedByName: string;
    reason: string;
    version: number;
  }>; // Track all rejections with version numbers

  // OTP document verification (HQ review)
  hqReviewStatus?: 'pending' | 'approved' | 'rejected';
  hqReviewedBy?: string;
  hqReviewedAt?: Timestamp;
  hqRejectionReason?: string;

  // Important dates
  reservationCompletedAt?: Timestamp;
  otpCompletedAt?: Timestamp;
  depositsCompletedAt?: Timestamp;
  dateSignedByClient?: Timestamp; // When client signed the reservation document
  dateSignedByPurchaser?: string; // When purchaser signed the OTP document
  dateSignedBySeller?: string; // When seller signed the OTP document
  // Write-once stats anchor: the FIRST purchaser-signed date, preserved across
  // OTP replacements/amendments so the OTP stat stays in its original month.
  otpFirstSignedByPurchaser?: string;
  
  // Metadata
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  lastModifiedBy?: string;

  // Lock states
  reservationLocked?: boolean;
  otpLocked?: boolean;
  depositsLocked?: boolean;

  // Reassessment tracking
  isReassessment?: boolean; // Is this deal currently in reassessment?
  reassessmentNumber?: number; // 1st, 2nd, 3rd reassessment (1-indexed)
  reassessmentStartedAt?: Timestamp; // When current reassessment started
  reassessmentStartedBy?: string; // User ID who triggered
  reassessmentStartedByName?: string; // User name
  reassessmentReason?: string; // Why reassessment needed
  originalFinanceStatus?: 'declined' | 'aip' | 'final_grant'; // Finance status before reassessment

  // Full reassessment history
  reassessmentHistory?: Array<{
    reassessmentNumber: number;
    startedAt: Timestamp;
    startedBy: string;
    startedByName: string;
    reason: string;
    originalStatus: 'declined' | 'aip' | 'final_grant'; // Status when reassessment started
    completedAt?: Timestamp; // When reassessment finished (new grant received)
    finalStatus?: 'declined' | 'aip' | 'final_grant'; // Final outcome
    banksTried?: string[]; // Banks attempted during this reassessment
  }>;
}

// Reservation amount status info
export interface ReservationAmountInfo {
  totalRequired: number;
  totalPaid: number;
  outstanding: number;
  payments: Payment[];
  lastPaymentDate?: string;
  isFullyPaid: boolean;
}

// Context type definition
interface DealContextType {
  // Real-time deal data
  deals: Map<string, DealCapture>;
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // Subscribe to specific deal
  subscribeToDeal: (unitId: string) => void;
  unsubscribeFromDeal: (unitId: string) => void;
  
  // Subscribe to all deals for a development
  subscribeToDevelopment: (developmentId: string) => void;
  unsubscribeFromDevelopment: (developmentId: string) => void;
  
  // Get deal with real-time updates
  getDeal: (unitId: string) => DealCapture | undefined;
  
  // Monitor reservation amount changes
  getReservationAmountStatus: (unitId: string) => ReservationAmountInfo | undefined;
  
  // Check if deal exists
  hasDeal: (unitId: string) => boolean;
  
  // Event listeners
  onDealUpdate: (unitId: string, callback: (deal: DealCapture) => void) => () => void;
  onStatusChange: (unitId: string, callback: (oldStatus: string, newStatus: string) => void) => () => void;
  onReservationAmountUpdate: (unitId: string, callback: (reservationAmounts: ReservationAmountInfo) => void) => () => void;
  
  // Force refresh
  refreshDeal: (unitId: string) => Promise<void>;
  refreshAllDeals: () => Promise<void>;
}

// Create context
const DealContext = createContext<DealContextType | undefined>(undefined);

// Custom hook to use the context
export const useDeal = () => {
  const context = useContext(DealContext);
  if (!context) {
    throw new Error('useDeal must be used within a DealProvider');
  }
  return context;
};

// Provider props
interface DealProviderProps {
  children: ReactNode;
}

// Event emitter for deal updates
class DealEventEmitter {
  private listeners: Map<string, Set<Function>> = new Map();
  
  on(event: string, callback: Function): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }
  
  emit(event: string, ...args: any[]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(...args));
    }
  }
  
  removeAllListeners(): void {
    this.listeners.clear();
  }
}

// Main Provider Component
export const DealProvider: React.FC<DealProviderProps> = ({ children }) => {
  // Use ref for deals to prevent unnecessary re-renders
  const dealsRef = useRef<Map<string, DealCapture>>(new Map());
  const [dealsVersion, setDealsVersion] = useState(0); // Version counter to trigger updates
  const subscriptionsRef = useRef<Map<string, Unsubscribe>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Event emitter instance
  const eventEmitterRef = useRef(new DealEventEmitter());
  const eventEmitter = eventEmitterRef.current;
  
  // Subscribe to a specific deal
  const subscribeToDeal = useCallback((unitId: string) => {
    const key = `deal-${unitId}`;
    
    // Don't subscribe if already subscribed
    if (subscriptionsRef.current.has(key)) {
      console.log(`Already subscribed to deal ${unitId}`);
      return;
    }
    
    console.log(`Subscribing to deal ${unitId}`);
    
    // Subscribe to the specific dealCapture document
    const unsubscribe = onSnapshot(
      doc(db, 'dealCaptures', unitId),
      (snapshot) => {
        if (snapshot.exists()) {
          const dealData = snapshot.data() as DealCapture;
          dealData.id = snapshot.id;
          
          const oldDeal = dealsRef.current.get(unitId);
          dealsRef.current.set(unitId, dealData);
          
          // Emit events
          eventEmitter.emit(`deal-update-${unitId}`, dealData);
          
          // Check for status change
          if (oldDeal && oldDeal.status !== dealData.status) {
            eventEmitter.emit(`status-change-${unitId}`, oldDeal.status || '', dealData.status || '');
          }
          
          // Check for deposit changes
          const oldReservationAmountTotal = (oldDeal?.reservationAmountPayments || []).reduce((sum, p) => sum + p.amount, 0);
          const newReservationAmountTotal = (dealData.reservationAmountPayments || []).reduce((sum, p) => sum + p.amount, 0);
          if (oldReservationAmountTotal !== newReservationAmountTotal) {
            const reservationAmountInfo = calculateReservationAmountInfo(dealData);
            eventEmitter.emit(`reservationAmount-update-${unitId}`, reservationAmountInfo);
          }
          
          // Increment version to trigger UI updates only when needed
          setDealsVersion(v => v + 1);
        } else {
          // Deal doesn't exist, remove from map
          dealsRef.current.delete(unitId);
          setDealsVersion(v => v + 1);
        }
      },
      (error) => {
        console.error(`Error listening to deal ${unitId}:`, error);
        setError((error instanceof Error ? error.message : String(error)));
      }
    );
    
    subscriptionsRef.current.set(key, unsubscribe);
  }, []);
  
  // Subscribe to all deals for a development
  const subscribeToDevelopment = useCallback((developmentId: string) => {
    const key = `dev-${developmentId}`;
    
    // Don't subscribe if already subscribed
    if (subscriptionsRef.current.has(key)) {
      console.log(`Already subscribed to development ${developmentId}`);
      return;
    }
    
    console.log(`Subscribing to all deals in development ${developmentId}`);
    
    // Query all dealCaptures for this development
    const q = query(
      collection(db, 'dealCaptures'),
      where('developmentId', '==', developmentId)
    );
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach(change => {
          const dealData = change.doc.data() as DealCapture;
          dealData.id = change.doc.id;
          const unitId = change.doc.id; // Document ID is the unit ID
          
          if (change.type === 'added' || change.type === 'modified') {
            const oldDeal = dealsRef.current.get(unitId);
            dealsRef.current.set(unitId, dealData);
            
            // Emit events
            eventEmitter.emit(`deal-update-${unitId}`, dealData);
            
            // Check for status change
            if (oldDeal && oldDeal.status !== dealData.status) {
              eventEmitter.emit(`status-change-${unitId}`, oldDeal.status || '', dealData.status || '');
            }
            
            // Check for deposit changes
            const oldReservationAmountTotal = (oldDeal?.reservationAmountPayments || []).reduce((sum, p) => sum + p.amount, 0);
            const newReservationAmountTotal = (dealData.reservationAmountPayments || []).reduce((sum, p) => sum + p.amount, 0);
            if (oldReservationAmountTotal !== newReservationAmountTotal) {
              const reservationAmountInfo = calculateReservationAmountInfo(dealData);
              eventEmitter.emit(`reservationAmount-update-${unitId}`, reservationAmountInfo);
            }
            
            // Only trigger re-render when data actually changes
            setDealsVersion(v => v + 1);
          } else if (change.type === 'removed') {
            dealsRef.current.delete(unitId);
            setDealsVersion(v => v + 1);
          }
        });
      },
      (error) => {
        console.error(`Error listening to development ${developmentId}:`, error);
        setError((error instanceof Error ? error.message : String(error)));
      }
    );
    
    subscriptionsRef.current.set(key, unsubscribe);
  }, []);
  
  // Unsubscribe from a deal
  const unsubscribeFromDeal = useCallback((unitId: string) => {
    const key = `deal-${unitId}`;
    const unsubscribe = subscriptionsRef.current.get(key);
    
    if (unsubscribe) {
      console.log(`Unsubscribing from deal ${unitId}`);
      unsubscribe();
      subscriptionsRef.current.delete(key);
    }
  }, []);
  
  // Unsubscribe from a development
  const unsubscribeFromDevelopment = useCallback((developmentId: string) => {
    const key = `dev-${developmentId}`;
    const unsubscribe = subscriptionsRef.current.get(key);
    
    if (unsubscribe) {
      console.log(`Unsubscribing from development ${developmentId}`);
      unsubscribe();
      subscriptionsRef.current.delete(key);
    }
  }, []);
  
  // Get a specific deal
  const getDeal = useCallback((unitId: string): DealCapture | undefined => {
    return dealsRef.current.get(unitId);
  }, [dealsVersion]); // Depend on version to get updates
  
  // Calculate reservation amount info
  const calculateReservationAmountInfo = (deal: DealCapture): ReservationAmountInfo => {
    const totalRequired = deal.reservationAmount || 0;
    const payments = deal.reservationAmountPayments || [];
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const outstanding = Math.max(0, totalRequired - totalPaid);
    
    const sortedPayments = [...payments].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    return {
      totalRequired,
      totalPaid,
      outstanding,
      payments,
      lastPaymentDate: sortedPayments[0]?.date,
      isFullyPaid: outstanding === 0
    };
  };
  
  // Get reservation amount status
  const getReservationAmountStatus = useCallback((unitId: string): ReservationAmountInfo | undefined => {
    const deal = dealsRef.current.get(unitId);
    if (!deal) return undefined;

    return calculateReservationAmountInfo(deal);
  }, [dealsVersion]); // Depend on version to get updates
  
  // Check if deal exists
  const hasDeal = useCallback((unitId: string): boolean => {
    return dealsRef.current.has(unitId);
  }, [dealsVersion]); // Depend on version to get updates
  
  // Event listener for deal updates
  const onDealUpdate = useCallback((unitId: string, callback: (deal: DealCapture) => void) => {
    return eventEmitter.on(`deal-update-${unitId}`, callback);
  }, []);
  
  // Event listener for status changes
  const onStatusChange = useCallback((unitId: string, callback: (oldStatus: string, newStatus: string) => void) => {
    return eventEmitter.on(`status-change-${unitId}`, callback);
  }, []);
  
  // Event listener for reservation amount updates
  const onReservationAmountUpdate = useCallback((unitId: string, callback: (reservationAmounts: ReservationAmountInfo) => void) => {
    return eventEmitter.on(`reservationAmount-update-${unitId}`, callback);
  }, []);
  
  // Force refresh a specific deal
  const refreshDeal = useCallback(async (unitId: string) => {
    console.log(`Force refreshing deal ${unitId}`);
    // Unsubscribe and resubscribe to force refresh
    unsubscribeFromDeal(unitId);
    setTimeout(() => subscribeToDeal(unitId), 100);
  }, [subscribeToDeal, unsubscribeFromDeal]);
  
  // Refresh all deals
  const refreshAllDeals = useCallback(async () => {
    console.log('Force refreshing all deals');
    setLoading(true);
    
    // Clear all subscriptions
    subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
    subscriptionsRef.current.clear();
    dealsRef.current.clear();
    setDealsVersion(v => v + 1);
    
    setLoading(false);
  }, []);
  
  // Cleanup all subscriptions on unmount
  useEffect(() => {
    return () => {
      console.log('DealProvider unmounting, cleaning up subscriptions');
      subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
      subscriptionsRef.current.clear();
      eventEmitter.removeAllListeners();
    };
  }, []);
  
  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo<DealContextType>(() => ({
    deals: dealsRef.current,
    loading,
    error,
    subscribeToDeal,
    unsubscribeFromDeal,
    subscribeToDevelopment,
    unsubscribeFromDevelopment,
    getDeal,
    getReservationAmountStatus,
    hasDeal,
    onDealUpdate,
    onStatusChange,
    onReservationAmountUpdate,
    refreshDeal,
    refreshAllDeals
  }), [
    dealsVersion, // Only re-create when deals change
    loading,
    error,
    subscribeToDeal,
    unsubscribeFromDeal,
    subscribeToDevelopment,
    unsubscribeFromDevelopment,
    getDeal,
    getReservationAmountStatus,
    hasDeal,
    onDealUpdate,
    onStatusChange,
    onReservationAmountUpdate,
    refreshDeal,
    refreshAllDeals
  ]);
  
  return (
    <DealContext.Provider value={value}>
      {children}
    </DealContext.Provider>
  );
};